import React, { useMemo, useState } from 'react';
import { Formik } from 'formik';
import styled from 'styled-components';
import { media } from 'src/styles/designs';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import {
  ResultsContainer,
  ShowcaseGallery,
} from 'src/modules/materials/components/ShowcasePage/ShowcaseGallery';
import { useUpdateEffect } from 'react-use';
import { dirRTL } from 'src/styles/theme';
import { useQueryParams } from 'src/hooks/useQueryParams';
import { omit, pick } from 'rambda';
import { UseQueryResult } from 'react-query';
import { FilterDescription } from 'src/components/filters/types';
import { Filters } from 'src/components/filters';
import {
  SearchFilters,
  searchFiltersExceptions,
  searchFiltersToQueryMap,
} from 'src/services/search';
import { FiltersBar } from 'src/modules/materials/components/ShowcasePage/FiltersBar';
import { ShowcaseSectionData } from 'src/modules/materials/models/showcase';
import {
  ShowcaseContext,
  ShowcaseContextValue,
} from 'src/modules/materials/providers/ShowcaseContext';
import {
  AnalyticsEvent,
  usePushAnalyticsEvent,
} from 'src/hooks/usePushAnalyticsEvent';

export interface ShowcasePageTemplateProps extends ShowcaseContextValue {
  filtersQuery: () => UseQueryResult<FilterDescription[]>;
  sectionsQuery: () => UseQueryResult<ShowcaseSectionData[]>;
}

const PageConstraintContainer = styled(ConstraintContainer)`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: calc(-1 * (161px));

    ${dirRTL} {
      margin-bottom: calc(-1 * (100px));
    }

    ${ResultsContainer} {
      margin-bottom: calc(150px);
      ${dirRTL} {
        margin-bottom: calc(100px);
      }
    }
  }
`;

const Results = styled.div`
  flex: 1 1 auto;

  ${media.w.greaterThanOrEqual.desktop} {
    display: grid;
    grid-template-areas: 'filters results';
    grid-template-columns: 300px 1fr;
  }
`;

export const ShowcasePageTemplate = ({
  filtersQuery,
  sectionsQuery,
  ...props
}: ShowcasePageTemplateProps) => {
  const [params, setParams] = useQueryParams<SearchFilters>();

  const showcaseFilterParamsKeys = useMemo(() => {
    const itemsToQuery =
      props.itemsToQuery ?? Object.keys(searchFiltersToQueryMap);

    return itemsToQuery.reduce<string[]>((prev, curr) => {
      return prev.concat(
        Object.keys(
          searchFiltersToQueryMap[curr as keyof typeof searchFiltersToQueryMap],
        ),
      );
    }, []);
  }, [props.itemsToQuery]);

  const queryParamsFilters = useMemo(
    () => pick(showcaseFilterParamsKeys, params),
    [params, showcaseFilterParamsKeys],
  );

  const [filterState, setFilterState] = useState(queryParamsFilters);
  const pushEvent = usePushAnalyticsEvent();

  useUpdateEffect(() => {
    const otherParams = omit(showcaseFilterParamsKeys, params);

    setParams({ ...otherParams, ...filterState });
  }, [filterState]);

  useUpdateEffect(() => {
    setFilterState(queryParamsFilters);
  }, [queryParamsFilters]);

  useUpdateEffect(() => {
    filterState.q &&
      pushEvent(AnalyticsEvent.Search, { phrase: filterState.q.toString() });
  }, [filterState.q, pushEvent]);

  const ShowcaseFilters = useMemo(() => Filters(filtersQuery), [filtersQuery]);

  return (
    <ShowcaseContext.Provider value={props}>
      <PageConstraintContainer>
        <Results>
          <Formik
            enableReinitialize
            initialValues={queryParamsFilters}
            onSubmit={(data, form) => {
              setFilterState(
                omit(searchFiltersExceptions as unknown as string[], data),
              );
              form.setSubmitting(false);
            }}
          >
            <FiltersBar>
              <ShowcaseFilters />
            </FiltersBar>
          </Formik>
          <ShowcaseGallery
            filters={filterState}
            sectionsQuery={sectionsQuery}
          />
        </Results>
      </PageConstraintContainer>
    </ShowcaseContext.Provider>
  );
};
